import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import useLeagueData from "../hooks/useLeagueData";
import useLeagueFixtures from "../hooks/useLeagueFixtures";
import TrophyCabinet from "../components/TrophyCabinet";
import AchievementGrid from "../components/AchievementGrid";
import ClubTimeline from "../components/ClubTimeline";
import { buildAchievements, buildClubPerformance, buildClubTimeline, buildTrophies } from "../services/achievementService";
import { subscribeSeasonHistory } from "../services/historyService";
import useLeagueVocabulary from "../hooks/useLeagueVocabulary";

export default function ClubProfile() {
  const { clubId } = useParams();
  const { orgId, season, clubs, standings, loading, error } = useLeagueData();
  const vocabulary = useLeagueVocabulary(orgId);
  const { fixtures } = useLeagueFixtures(orgId, season.id, season.currentRound || 1);
  const club = clubs.find((item) => item.id === clubId);
  const [archives, setArchives] = useState([]);

  useEffect(() => {
    if (!orgId) return undefined;
    return subscribeSeasonHistory(orgId, (items) => {
      setArchives(items.filter((item) => item.status === "archived" || Array.isArray(item.finalStandings)));
    }, () => setArchives([]));
  }, [orgId]);
  const stats = useMemo(() => buildClubPerformance(club, fixtures), [club, fixtures]);
  const achievements = useMemo(() => buildAchievements(club, fixtures, standings, season), [club, fixtures, standings, season]);
  const trophies = useMemo(() => buildTrophies(club, archives), [club, archives]);
  const timeline = useMemo(() => buildClubTimeline(club, fixtures, achievements, trophies), [club, fixtures, achievements, trophies]);
  const position = standings.findIndex((entry) => entry.id === clubId) + 1;

  if (!loading && !club) return <Navigate to="/league" replace />;

  return (
    <div className="league-home">
      <Link className="league-back-link" to="/league">← Back to League Centre</Link>
      {error ? <div className="league-alert">{error}</div> : null}
      <section className="league-club-hero">
        <div className="league-club-badge">{club?.badge || club?.name?.charAt(0) || "M"}</div>
        <div><p className="league-eyebrow">{season.divisionName} · {vocabulary.singular} profile</p><h1>{club?.name || `Loading ${vocabulary.singularLower}`}</h1><p>{club?.siteId ? `${vocabulary.singular} ${club.siteId}` : `Matrix League ${vocabulary.singularLower}`}</p></div>
        <div className="league-club-position"><span>Current position</span><strong>{position || "–"}</strong><small>of {standings.length || 0} {vocabulary.pluralLower}</small></div>
      </section>

      <section className="league-kpis league-profile-kpis">
        <article><span>Played</span><strong>{stats.played}</strong><small>{stats.wins}W · {stats.draws}D · {stats.losses}L</small></article>
        <article><span>Performance difference</span><strong>{stats.goalDifference > 0 ? `+${stats.goalDifference}` : stats.goalDifference}</strong><small>{stats.goalsFor} earned · {stats.goalsAgainst} compared</small></article>
        <article><span>Operational score</span><strong>{stats.averageOperationalScore}%</strong><small>Current season average</small></article>
        <article><span>Best streak</span><strong>{stats.longestWinningStreak}</strong><small>Consecutive wins</small></article>
      </section>

      <section className="league-panel league-profile-section">
        <div className="league-panel-head"><div><p>Current season</p><h2>Form and performance</h2></div></div>
        <div className="league-profile-performance">
          <div><span>Last five</span><div className="league-form-large">{stats.currentForm.length ? stats.currentForm.map((result, index) => <b key={`${result}-${index}`} className={result.toLowerCase()}>{result}</b>) : <em>No results</em>}</div></div>
          <div><span>Hosted events</span><strong>{stats.homePlayed}</strong></div>
          <div><span>External events</span><strong>{stats.awayPlayed}</strong></div>
          <div><span>Clean sheets</span><strong>{stats.cleanSheets}</strong></div>
          <div><span>Unbeaten run</span><strong>{stats.longestUnbeatenRun}</strong></div>
        </div>
      </section>

      <TrophyCabinet trophies={trophies} />
      <AchievementGrid achievements={achievements} />
      <ClubTimeline events={timeline} />
    </div>
  );
}
